<template>
  <div class="p-organization-list">
    <template v-if="data.trial.active.length > 0">
      <div class="p-organization-list__title">トライアルプラン</div>
      <ListItemOrganization
        v-for="item in trialActiveOrderByExpireDateAsc"
        :key="item.organizationId"
        :item="item"
        :isActive="true"
        planTitle="トライアルプラン"
      >
        <!-- 有効なトライアルプランには有効期限を延長する導線を追加 -->
        <template v-slot:button>
          <dd class="p-organization-list-date__edit" @click.stop>
            <Button class="edit" @click.stop="openEditModal(item)">
              編集する
            </Button>
            <div
              class="p-organization-list-date__modal"
              v-if="item.organizationId === editSelected.organizationId"
            >
              <v-date-picker
                v-model="editSelected.subscription.expirationDate"
                :min-date="item.subscription.expirationDate"
              />
              <div class="p-organization-list-date__modalButtons">
                <Button variant="light" fsize="f12" @click="closeEditModal"
                  >キャンセル</Button
                >
                <div class="c-alertPos">
                  <Button
                    variant="main"
                    fsize="f12"
                    @click="editExpritationDate"
                    >有効期限を延長</Button
                  >
                  <Alert v-if="hasError"
                    >有効期限の更新に失敗しました。開発チームまでお問い合わせください。</Alert
                  >
                </div>
              </div>
            </div>
          </dd>
        </template>
      </ListItemOrganization>
    </template>
    <template v-if="data.trial.disable.length > 0">
      <div class="p-organization-list__title">
        トライアルプラン（有効期限切れ）
      </div>
      <ListItemOrganization
        v-for="item in trialDisableOrderByExpireDateDesc"
        :key="item.organizationId"
        :item="item"
        :isActive="false"
        planTitle="無料トライアルプラン"
      />
    </template>

    <template
      v-if="data.card.active.length > 0 || data.invoice.active.length > 0"
    >
      <div class="p-organization-list__title">有料プラン</div>
      <ListItemOrganization
        v-for="item in data.card.active"
        :key="item.organizationId"
        :item="item"
        :isActive="true"
        planTitle="カード払いプラン"
      />
      <ListItemOrganization
        v-for="item in data.invoice.active"
        :key="item.organizationId"
        :item="item"
        :isActive="true"
        planTitle="請求書プラン"
      />
    </template>
  </div>
</template>

<script>
import apiOrganizations from '@/api/organizations';
import utils from '@/utils';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import ListItemOrganization from '@/components/organization/ListItemOrganization';
import { SUBSCRIPTION_PLAN_ID } from '@/constants';
import * as dayjs from 'dayjs';
import 'dayjs/locale/ja';

export default {
  props: {
    items: {
      type: Array,
      required: true
    }
  },

  components: {
    ListItemOrganization,
    Alert,
    Button
  },

  watch: {
    hasError: {
      // エラーメッセージを消すのに丁度いいトリガーがないので、3秒後に消すようにする
      handler() {
        if (this.hasError) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.hasError = false;
            }, 3000);
          });
        }
      }
    }
  },

  data() {
    return {
      data: {
        card: { active: [], disable: [] },
        invoice: { active: [], disable: [] },
        trial: { active: [], disable: [] }
        // 複数の有効期限延長モーダルを同時に開けないよう、開いている組織のIDを認識しておく
      },
      editSelected: {},
      hasError: false,
      datePicker: {
        popover: {
          transition: 'fade',
          visibility: 'focus'
        }
      }
    };
  },

  computed: {
    trialActiveOrderByExpireDateAsc() {
      return this.data.trial.active.slice().sort(function(a, b) {
        return (
          new Date(a.subscription.expirationDate) -
          new Date(b.subscription.expirationDate)
        );
      });
    },
    trialDisableOrderByExpireDateDesc() {
      return this.data.trial.disable.slice().sort(function(a, b) {
        return (
          new Date(b.subscription.expirationDate) -
          new Date(a.subscription.expirationDate)
        );
      });
    }
  },

  created() {
    for (let i = 0; i < this.items.length; i++) {
      if (this.isCard(this.items[i].planId)) {
        // カード契約プランのユーザーは有効期限を取得しない
        this.data.card.active.push(this.items[i]);
      } else if (this.isInvoice(this.items[i].planId)) {
        // 請求書プランのユーザーは有効期限を取得しない
        this.data.invoice.active.push(this.items[i]);
      } else if (this.isTrial(this.items[i].planId)) {
        // 無料トライアルプランのユーザーは有効期限を取得する!
        this.addSubscriptions(this.items[i], 'trial');
      }
    }
  },

  methods: {
    // 有料ユーザー: 請求書プラン
    isInvoice(planId) {
      return planId === SUBSCRIPTION_PLAN_ID.invoice ||
        planId === SUBSCRIPTION_PLAN_ID.invoice202305 ||
        planId === SUBSCRIPTION_PLAN_ID.invoiceLightPlan
        ? true
        : false;
    },

    // 有料ユーザー: カード払い
    isCard(planId) {
      const keys = Object.keys(SUBSCRIPTION_PLAN_ID.stripe);
      for (let i = 0; i < keys.length; i++) {
        if (planId === SUBSCRIPTION_PLAN_ID.stripe[keys[i]]) {
          return true;
        }
      }
      return false;
    },

    // 無料ユーザー: トライアル
    isTrial(planId) {
      return planId === SUBSCRIPTION_PLAN_ID.trial ? true : false;
    },

    // 契約情報を追加
    addSubscriptions(item, type) {
      return apiOrganizations.subscriptions
        .get(this.auth.idToken, item.organizationId)
        .then(response => {
          // 取得したサブスクリプション情報を追加
          item.subscription =
            response.data.subscriptions.base.length &&
            response.data.subscriptions.base[0].expirationDate
              ? response.data.subscriptions.base[0]
              : null;

          // 有効期限日と当日の0時を比較（有効期限日はタイムゾーンをもっていないので、そのまま日本日付として使用）
          if (
            item.subscription &&
            dayjs()
              .startOf('day')
              .valueOf() <= dayjs(item.subscription.expirationDate).valueOf()
          ) {
            // 有効期限がきれていない場合
            this.data[type].active.push(item);
          } else {
            // 有効期限がきれている場合
            this.data[type].disable.push(item);
          }
        })
        .catch(error => {
          this.data[type].disable.push(item);
        });
    },

    openEditModal(item) {
      // 階層構造を持ってるので組織の情報を深いコピー
      this.editSelected = JSON.parse(JSON.stringify(item));
    },

    closeEditModal() {
      this.editSelected = {};
    },

    editExpritationDate() {
      const expirationDate = this.editSelected.subscription.expirationDate;
      const stringExprirationDate = utils.formatDate(
        expirationDate,
        'YYYY-MM-DD'
      );
      const params = {
        expirationDate: stringExprirationDate
      };
      return apiOrganizations.subscriptions
        .put(
          params,
          this.auth.idToken,
          this.editSelected.organizationId,
          this.editSelected.subscription.id
        )
        .then(response => {
          // 更新に成功したら、一覧で表示されている有効期限の日付をアップデート
          const index = this.data.trial.active.findIndex(
            e => e.organizationId === this.editSelected.organizationId
          );
          this.data.trial.active[
            index
          ].subscription.expirationDate = stringExprirationDate;
          this.closeEditModal();
        })
        .catch(error => {
          this.hasError = true;
        });
    }
  }
};
</script>
